export interface UserRecord {
  id: number;
  fullName: string;
  homeName?: string | null;
  email: string;
  gender: string;
  birthDay?: number | null;
  birthMonth?: number | null;
  birthYear?: number | null;
  diedAt?: string | null;
  diedYear?: number | null;
  isAlive?: boolean;
  parentId?: number | null;
  generation?: number;
  familyNameId: number;
  imageUrl?: string | null;
  spouseName?: string | null;
  spouseImageUrl?: string | null;
  spouseBirthDay?: number | null;
  spouseBirthMonth?: number | null;
  spouseBirthYear?: number | null;
  spouseDiedAt?: string | null;
  spouseDiedYear?: number | null;
  spouseIsAlive?: boolean;
  isAdmin: boolean;
}

export interface UserTreeNode extends UserRecord {
  children: UserTreeNode[];
  spouse?: UserTreeNode | null;
  spouseName?: string | null;
  spouseImageUrl?: string | null;
  spouseBirthDay?: number | null;
  spouseBirthMonth?: number | null;
  spouseBirthYear?: number | null;
}

export const isSystemAdmin = (user: UserRecord): boolean => user.isAdmin;

type CurrentUserPermissions = {
  id?: number;
  isAdmin?: boolean;
  isFamilyAdmin?: boolean;
  familyNameId?: number;
} | null | undefined;

type TargetUserPermissions = {
  id?: number;
  isAdmin?: boolean;
  familyNameId?: number;
  parentId?: number | null;
};

export const canViewUser = (
  currentUser: CurrentUserPermissions,
  user: TargetUserPermissions,
  allUsers: TargetUserPermissions[] = []
): boolean => {
  if (!currentUser) {
    return false;
  }

  if (currentUser.isAdmin) {
    return true;
  }

  if (user.isAdmin) {
    return false;
  }

  if (user.id === currentUser.id) {
    return true;
  }

  const usersById = new Map(allUsers.map((candidate) => [candidate.id, candidate]));
  let parentId = user.parentId;
  while (parentId) {
    if (parentId === currentUser.id) {
      return true;
    }
    parentId = usersById.get(parentId)?.parentId;
  }

  return currentUser.isFamilyAdmin && user.familyNameId === currentUser.familyNameId;
};

const normalizeName = (value?: string | null): string => (value ?? '').trim().toLowerCase();

const findSpouseMatch = (user: UserRecord, allUsers: UserRecord[]): UserRecord | undefined => {
  const spouseName = normalizeName(user.spouseName);
  if (spouseName) {
    const directMatch = allUsers.find(
      (candidate) =>
        candidate.id !== user.id &&
        !isSystemAdmin(candidate) &&
        normalizeName(candidate.fullName) === spouseName
    );
    if (directMatch) {
      return directMatch;
    }
  }

  const candidateMatches = allUsers.filter(
    (candidate) =>
      candidate.id !== user.id &&
      !isSystemAdmin(candidate) &&
      normalizeName(candidate.spouseName) === normalizeName(user.fullName)
  );

  if (candidateMatches.length > 0) {
    return candidateMatches[0];
  }

  const otherFamilyCandidates = allUsers.filter(
    (candidate) =>
      candidate.id !== user.id &&
      !isSystemAdmin(candidate) &&
      String(candidate.familyNameId) !== String(user.familyNameId)
  );

  if (otherFamilyCandidates.length === 1) {
    return otherFamilyCandidates[0];
  }

  return undefined;
};

export const getVisibleFamilyTreeUsers = (
  currentUser: CurrentUserPermissions,
  users: UserRecord[],
  familyId?: number | string | null
): UserRecord[] => {
  const targetFamilyId = familyId ?? currentUser?.familyNameId;

  if (!targetFamilyId) {
    return [];
  }

  const currentFamilyUsers = users.filter(
    (user) => !isSystemAdmin(user) && String(user.familyNameId) === String(targetFamilyId)
  );

  const spouseMatches = currentFamilyUsers.flatMap((user) => {
    const spouse = findSpouseMatch(user, users);
    return spouse && String(spouse.familyNameId) !== String(targetFamilyId) ? [spouse] : [];
  });

  const uniqueSpouseMatches = spouseMatches.filter(
    (candidate, index, arr) => arr.findIndex((item) => item.id === candidate.id) === index
  );

  const visibleUsers = [...currentFamilyUsers, ...uniqueSpouseMatches];

  if (!currentUser || currentUser.isAdmin) {
    return visibleUsers;
  }

  return visibleUsers;
};

export const canEditUser = (
  currentUser: CurrentUserPermissions,
  user: TargetUserPermissions,
  allUsers: TargetUserPermissions[] = []
): boolean => {
  if (!currentUser) {
    return false;
  }

  if (currentUser.isAdmin) {
    return true;
  }

  if (user.isAdmin) {
    return false;
  }

  if (currentUser.isFamilyAdmin && user.familyNameId === currentUser.familyNameId) {
    return true;
  }

  return canViewUser(currentUser, user, allUsers);
};

export const canCreateUser = (currentUser: CurrentUserPermissions): boolean => Boolean(currentUser);

export const canAccessUsersPage = (currentUser: CurrentUserPermissions): boolean => Boolean(currentUser);

export const canDeleteUser = (
  currentUser: CurrentUserPermissions,
  user: TargetUserPermissions,
  allUsers: TargetUserPermissions[] = []
): boolean => {
  if (!currentUser || currentUser.id === user.id) {
    return false;
  }

  if (currentUser.isAdmin) {
    return true;
  }

  if (user.isAdmin) {
    return false;
  }

  return canViewUser(currentUser, user, allUsers);
};

const birthSortValue = (user: UserTreeNode): number =>
  user.birthYear ? user.birthYear * 10000 + (user.birthMonth ?? 99) * 100 + (user.birthDay ?? 99) : Number.MAX_SAFE_INTEGER;

const sortByDateOfBirth = (a: UserTreeNode, b: UserTreeNode) => birthSortValue(a) - birthSortValue(b);

const sortTree = (nodes: UserTreeNode[]) => {
  nodes.sort(sortByDateOfBirth);
  nodes.forEach((node) => sortTree(node.children));
};

export const buildFamilyTree = (users: UserRecord[], allUsers: UserRecord[] = users): UserTreeNode[] => {
  const members = users.filter((user) => !isSystemAdmin(user));
  const memberIds = new Set(members.map((user) => user.id));
  const spouseIds = new Set<number>();

  const nodeMap = new Map<number, UserTreeNode>();
  members.forEach((user) => {
    nodeMap.set(user.id, { ...user, children: [] });
  });

  members.forEach((user) => {
    const spouse = findSpouseMatch(user, allUsers);
    const node = nodeMap.get(user.id);
    const spouseNode = spouse ? nodeMap.get(spouse.id) : undefined;
    if (node && spouseNode && spouseNode.id !== node.id) {
      node.spouse = spouseNode;
      spouseIds.add(spouseNode.id);
    }
  });

  const roots: UserTreeNode[] = [];
  const placedIds = new Set<number>();

  members.forEach((user) => {
    const node = nodeMap.get(user.id)!;

    if (placedIds.has(node.id) || spouseIds.has(node.id)) {
      return;
    }

    if (user.parentId && memberIds.has(user.parentId)) {
      const parent = nodeMap.get(user.parentId)!;
      if (!parent.children.some((child) => child.id === node.id)) {
        parent.children.push(node);
      }
      placedIds.add(node.id);
      return;
    }

    roots.push(node);
    placedIds.add(node.id);
  });

  sortTree(roots);
  return roots;
};

export const getDescendantFamilyTreeUsers = (users: UserRecord[], rootId: number): UserRecord[] => {
  const descendants = new Map<number, UserRecord[]>();
  users.forEach((user) => {
    if (user.parentId) {
      const children = descendants.get(user.parentId) || [];
      children.push(user);
      descendants.set(user.parentId, children);
    }
  });

  const result: UserRecord[] = [];
  const visit = (userId: number) => {
    const user = users.find((candidate) => candidate.id === userId);
    if (!user || result.some((item) => item.id === user.id)) {
      return;
    }

    result.push(user);
    (descendants.get(userId) || []).forEach((child) => visit(child.id));
  };

  visit(rootId);
  return result;
};

export const countTreeNodes = (nodes: UserTreeNode[]): number =>
  nodes.reduce((total, node) => total + 1 + countTreeNodes(node.children), 0);
